import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Boxes, CircleAlert, Package, PackageCheck, Plus, Wrench } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CurrencyValue, DataTable, FormDialog, PageHeader, StatCard, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useAssetCategories, useAssetMutations, useAssetSummary, useAssets } from '../api'
import { ASSET_STATUS_LABELS, CONDITION_LABELS } from '../types'
import type { AssetCondition, AssetStatus, AssetView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<AssetStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  IN_STOCK: 'success',
  ASSIGNED: 'info',
  IN_MAINTENANCE: 'warning',
  LOST: 'danger',
  DISPOSED: 'neutral',
}

/** The register: everything the company owns, who has it, and what it is worth. */
export function AssetRegisterPage() {
  useBreadcrumb()
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState<string | undefined>('assetTag,asc')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [categoryId, setCategoryId] = useState('')

  const assets = useAssets({ page, size, sort, q: search, status, categoryId })
  const summary = useAssetSummary()
  const categories = useAssetCategories()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const locations = useLookup<{ id: string; name: string }>('locations')
  const { saveAsset } = useAssetMutations()

  const [draft, setDraft] = useState<{
    categoryId: string; assetTag: string; name: string; serialNumber: string
    purchaseDate: string; purchaseCost: string; currency: string; recoveryValue: string
    condition: AssetCondition; locationId: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const columns = useMemo<ColumnDef<AssetView, unknown>[]>(() => [
    {
      id: 'assetTag',
      header: 'Tag',
      accessorFn: (row) => row.assetTag,
      cell: ({ row }) => (
        <button
          type="button"
          className="font-mono underline underline-offset-2"
          onClick={() => { void navigate(`/assets/${row.original.id}`) }}
        >
          {row.original.assetTag}
        </button>
      ),
    },
    { id: 'name', header: 'Item', accessorFn: (row) => row.name },
    { id: 'categoryName', header: 'Category', accessorFn: (row) => row.categoryName ?? '—' },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={STATUS_TONE[row.original.status as AssetStatus] ?? 'neutral'}
          label={ASSET_STATUS_LABELS[row.original.status as AssetStatus] ?? row.original.status}
        />
      ),
    },
    {
      id: 'heldByName',
      header: 'Held by',
      accessorFn: (row) => row.heldByName ?? '—',
      cell: ({ row }) => row.original.heldByName
        ? <span>{row.original.heldByName} <span className="text-slate-500">since {row.original.heldSince}</span></span>
        : <span className="text-slate-500">Nobody</span>,
    },
    {
      id: 'condition',
      header: 'Condition',
      accessorFn: (row) => CONDITION_LABELS[row.condition as AssetCondition] ?? row.condition,
    },
    {
      id: 'bookValue',
      header: 'Book value',
      accessorFn: (row) => row.bookValue,
      cell: ({ row }) => row.original.bookValue == null
        ? <span className="text-slate-500" title={row.original.valuationNote ?? ''}>Not computable</span>
        : <CurrencyValue value={row.original.bookValue} currency={row.original.currency ?? undefined} />,
    },
  ], [navigate])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Asset register"
        subtitle="What the company owns, who is holding it, and what it is worth today."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                categoryId: categories.data?.[0]?.id ?? '', assetTag: '', name: '',
                serialNumber: '', purchaseDate: '', purchaseCost: '', currency: 'AED',
                recoveryValue: '', condition: 'GOOD', locationId: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New asset
          </Button>
        }
      />

      {summary.isSuccess && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard label="Assets" value={summary.data.total} icon={Boxes} />
            <StatCard label="In stock" value={summary.data.inStock} icon={PackageCheck} />
            <StatCard label="With somebody" value={summary.data.assigned} icon={Package} />
            <StatCard label="In maintenance" value={summary.data.inMaintenance} icon={Wrench} />
            <StatCard
              label="Lost" value={summary.data.lost} icon={CircleAlert}
              tint={summary.data.lost > 0 ? 'red' : undefined}
            />
          </div>
          <Card className="p-4 text-sm text-slate-700">
            <p>
              {summary.data.bookValue == null
                ? 'No book value can be totalled.'
                : (
                  <>
                    Book value today:{' '}
                    <CurrencyValue
                      value={summary.data.bookValue}
                      currency={summary.data.currency ?? undefined}
                    />
                    .
                  </>
                )}{' '}
              {summary.data.valuationNote}
            </p>
          </Card>
        </>
      )}

      <DataTable<AssetView>
        columns={columns}
        data={assets.data}
        isLoading={assets.isPending}
        error={assets.error}
        onRetry={() => void assets.refetch()}
        page={page}
        size={size}
        sort={sort}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSortChange={setSort}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        emptyTitle="No asset is on the register"
        emptyDescription="Add what the company owns. A category decides how it depreciates and what licence its holder needs."
        toolbar={
          <>
            <Select
              aria-label="Status"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0) }}
            >
              <option value="">Any status</option>
              {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              aria-label="Category"
              value={categoryId}
              onChange={(event) => { setCategoryId(event.target.value); setPage(0) }}
            >
              <option value="">Any category</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </>
        }
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title="New asset"
        description="The tag is what somebody reads off the label during a count, so it has to be unique."
        submitLabel="Save asset"
        loading={saveAsset.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveAsset.mutateAsync({
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  categoryId: draft.categoryId,
                  assetTag: draft.assetTag,
                  name: draft.name,
                  serialNumber: draft.serialNumber || null,
                  purchaseDate: draft.purchaseDate || null,
                  purchaseCost: draft.purchaseCost ? Number(draft.purchaseCost) : null,
                  currency: draft.purchaseCost ? draft.currency : null,
                  recoveryValue: draft.recoveryValue ? Number(draft.recoveryValue) : null,
                  condition: draft.condition,
                  locationId: draft.locationId || null,
                  customFields: {},
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That asset could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="asset-category">Category</Label>
              <Select
                id="asset-category"
                className="w-full"
                value={draft.categoryId}
                onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
              >
                <option value="">Choose a category…</option>
                {categories.data?.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="asset-tag">Asset tag</Label>
              <Input
                id="asset-tag" value={draft.assetTag}
                onChange={(event) => setDraft({ ...draft, assetTag: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="asset-name">What it is</Label>
              <Input
                id="asset-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="asset-serial">Serial number</Label>
              <Input
                id="asset-serial" value={draft.serialNumber}
                onChange={(event) => setDraft({ ...draft, serialNumber: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="asset-location">Location</Label>
              <Select
                id="asset-location"
                className="w-full"
                value={draft.locationId}
                onChange={(event) => setDraft({ ...draft, locationId: event.target.value })}
              >
                <option value="">No location</option>
                {locations.data?.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="asset-purchased">Bought on</Label>
              <DatePicker
                id="asset-purchased" value={draft.purchaseDate}
                onChange={(event) => setDraft({ ...draft, purchaseDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="asset-cost">Purchase cost</Label>
              <Input
                id="asset-cost" type="number" value={draft.purchaseCost}
                onChange={(event) => setDraft({ ...draft, purchaseCost: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Without a cost and a date there is no book value, whatever the category says.
              </p>
            </div>
            <div>
              <Label htmlFor="asset-recovery">Recovery value</Label>
              <Input
                id="asset-recovery" type="number" value={draft.recoveryValue}
                onChange={(event) => setDraft({ ...draft, recoveryValue: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                What to charge somebody who does not return it, if the settlement terms are set
                to use it. No default.
              </p>
            </div>
            <div>
              <Label htmlFor="asset-condition">Condition</Label>
              <Select
                id="asset-condition"
                className="w-full"
                value={draft.condition}
                onChange={(event) =>
                  setDraft({ ...draft, condition: event.target.value as AssetCondition })}
              >
                {Object.entries(CONDITION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
